import { FC, useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Alert, Box, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Link } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { PlatformCard } from './PlatformCard';
import { IPlatformAdmin } from './PlatformLayout';
import PlanFormDialog from './PlanFormDialog';
import { errorMessage, formatLimit, formatMoney, platformApi } from './platformApi';

const PlatformPlans: FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState('');
  const [missing, setMissing] = useState<string[]>([]);
  // null = closed, {} = add, { plan } = edit
  const [form, setForm] = useState<{ plan?: any; product?: 'erp' | 'pos' } | null>(null);
  const isOwner = useOutletContext<IPlatformAdmin | undefined>()?.role === 'owner';

  const load = useCallback(() => {
    platformApi.get('/plans')
      .then((response) => { setPlans(response.data.plans); setMissing(response.data.missingStandard || []); setLoaded(true); })
      .catch((err) => setError(errorMessage(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Creates the standard plans that are missing; existing plans and accounts are not changed
  const sync = async () => {
    setError('');
    setSyncing(true);
    try {
      const response = await platformApi.post('/plans/sync');
      const created = response.data.result.filter((r: any) => r.action !== 'kept').length;
      setSynced(`${created} standard plan(s) created.`);
      load();
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const erp = plans.filter((p) => p.product !== 'pos');
  const pos = plans.filter((p) => p.product === 'pos');
  const price = (p: any, cycle: string) => (p.pricing ? formatMoney(p.pricing[cycle]) : 'Contract');
  const storage = (mb?: number) => (mb === undefined || mb === null ? '-' : mb < 0 ? 'Unlimited' : mb >= 1024 ? `${mb / 1024} GB` : `${mb} MB`);
  const addButton = (product: 'erp' | 'pos') => (isOwner && plans.some((p) => p.product === product)
    ? <Button size='small' variant='contained' onClick={() => setForm({ product })} sx={{ textTransform: 'none', height: 28 }}>{product === 'pos' ? 'Add POS plan' : 'Add ERP plan'}</Button>
    : undefined);
  const editButton = (p: any) => (
    <Button size='small' variant='outlined' onClick={() => setForm({ plan: p })} sx={{ textTransform: 'none', height: 26 }}>Edit</Button>
  );
  const planNote = (p: any) => {
    const note = p.forAccount
      ? <>Custom · <Link to={`/platform/accounts/${p.forAccount._id}`} style={{ color: '#059669', textDecoration: 'none' }}>{p.forAccount.name}</Link></>
      : p.isTemplate ? 'Template for contracts' : p.isPublic === false ? 'Private' : null;
    const inactive = p.status === 'inactive' ? ' · Inactive' : '';
    return note || inactive ? <div style={{ color: '#6b7280', fontSize: 12 }}>{note}{inactive}</div> : null;
  };
  const compact = { '& th, & td': { whiteSpace: 'nowrap', px: 1.5 }, '& th': { fontWeight: 600, color: '#6b7280' } };

  return (
    <>
      {isOwner && missing.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <LoadingButton size='small' variant='outlined' loading={syncing} onClick={sync} sx={{ textTransform: 'none', bgcolor: '#fff' }}>
            Create standard plans
          </LoadingButton>
        </Box>
      )}
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      {synced && <Alert severity='success' sx={{ mb: 2 }}>{synced}</Alert>}
      {loaded && missing.length > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Missing standard plans: {missing.join(', ')}. Click "Create standard plans" to add them. Existing plans and customer accounts are not changed.
        </Alert>
      )}
      <PlatformCard title="ERP plans (held by the account owner, shared by all their companies)" action={addButton('erp')}>
        <Table size='small' sx={compact}>
          <TableHead>
            <TableRow>
              <TableCell>Plan</TableCell>
              <TableCell align='right'>Monthly</TableCell>
              <TableCell align='right'>Yearly</TableCell>
              <TableCell align='right'>Users</TableCell>
              <TableCell align='right'>Locations</TableCell>
              <TableCell align='right'>Companies / owner</TableCell>
              <TableCell align='right'>Invoices/mo</TableCell>
              <TableCell align='right'>Items</TableCell>
              <TableCell align='right'>Employees</TableCell>
              <TableCell align='right'>Storage</TableCell>
              <TableCell align='right'>Modules</TableCell>
              <TableCell align='right'>Owners</TableCell>
              {isOwner && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {erp.map((p) => (
              <TableRow key={p.code}>
                <TableCell>
                  {p.name}
                  {planNote(p)}
                </TableCell>
                <TableCell align='right'>{price(p, 'monthly')}</TableCell>
                <TableCell align='right'>{price(p, 'yearly')}</TableCell>
                {['users', 'locations', 'companies', 'invoicesPerMonth', 'items', 'employees'].map((key) => (
                  <TableCell align='right' key={key}>{formatLimit(p.limits?.[key])}</TableCell>
                ))}
                <TableCell align='right'>{storage(p.limits?.storageMB)}</TableCell>
                <TableCell align='right'>{(p.modules || []).length}</TableCell>
                <TableCell align='right'>{p.subscriptions}</TableCell>
                {isOwner && <TableCell align='right'>{editButton(p)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PlatformCard>
      <div style={{ height: 16 }} />
      <PlatformCard title="POS plans (per company and outlet, needs the owner's ERP plan)" action={addButton('pos')}>
        <Table size='small' sx={compact}>
          <TableHead>
            <TableRow>
              <TableCell>Plan</TableCell>
              <TableCell align='right'>Yearly per outlet</TableCell>
              <TableCell>ERP modules unlocked</TableCell>
              <TableCell align='right'>Companies</TableCell>
              {isOwner && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {pos.map((p) => (
              <TableRow key={p.code}>
                <TableCell>{p.name}{planNote(p)}</TableCell>
                <TableCell align='right'>{price(p, 'yearly')}</TableCell>
                <TableCell sx={{ whiteSpace: 'normal !important' }}>{(p.grantsErpModules || []).filter((m: string) => m.startsWith('inventory.')).map((m: string) => m.replace('inventory.', '')).join(', ')}</TableCell>
                <TableCell align='right'>{p.subscriptions}</TableCell>
                {isOwner && <TableCell align='right'>{editButton(p)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PlatformCard>
      {form && (
        <PlanFormDialog
          plan={form.plan}
          product={form.plan ? undefined : form.product}
          basePlans={plans.filter((p) => !p.forAccount)}
          onClose={() => setForm(null)}
          onSaved={() => { setSynced(form.plan ? 'Plan saved.' : 'Plan created.'); load(); }}
        />
      )}
    </>
  );
};

export default PlatformPlans;
